import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { languageFromPath, withLang } from '../i18n/i18n'
import { useDocumentMeta } from '../i18n/useDocumentMeta'

// Privacy policy. Content lives in src/i18n/locales/en/privacy.json (English
// authored; other locales fall back to English via i18next fallbackLng — same
// accepted precedent as the 'promise' namespace on RefundPolicy).
//
// Written in the conventional register of the category (cf. Airalo, Holafly):
// recipients are described as CATEGORIES ("payment service providers",
// "connectivity providers"), never named vendors — GDPR Art. 13 explicitly
// permits categories of recipients, and naming suppliers is a business
// disclosure we choose not to make. Keep it that way.
//
// The content is still written from the code, not from a template. The claims
// most worth being able to defend remain verifiable in the backend: the
// visitor's IP is used to derive a country and never persisted (track.py),
// and connectivity providers receive only the email + plan details. If either
// stops being true, this page must change with it.
//
// Rendering is a generic section list so the copy can be restructured in the
// JSON without touching this file: each section has a title, optional body,
// optional intro + items (lead/body pairs), optional footer. body and footer
// may carry a mailto link, so they render as HTML — the strings are ours,
// in-repo, never user-supplied.

interface SectionItem {
  lead: string
  body: string
}
interface Section {
  title: string
  body?: string
  intro?: string
  items?: SectionItem[]
  footer?: string
}

export default function Privacy() {
  const location = useLocation()
  const { t } = useTranslation('privacy')
  const lang = languageFromPath(location.pathname)
  useDocumentMeta({ title: t('seo.title'), description: t('seo.description') })

  const sections = t('sections', { returnObjects: true }) as Section[]

  return (
    <div className="faq">
      <section className="faq-hero">
        <div className="faq-hero-inner">
          <div className="breadcrumb">
            <Link to={withLang('/', lang)} style={{ color: 'var(--ink-3)' }}>
              {t('nav.home', { ns: 'common' })}
            </Link>{' '}
            / <span style={{ color: 'var(--ink)' }}>{t('breadcrumb')}</span>
          </div>
          <div className="eyebrow" style={{ marginTop: 16 }}>{t('eyebrow')}</div>
          <h1 className="faq-hero-h1">{t('title')}</h1>
          <p className="lede faq-hero-lede">{t('lede')}</p>
          <p className="muted" style={{ fontSize: 13, marginTop: 12 }}>{t('updated')}</p>
        </div>
      </section>

      {sections.map((s, idx) => (
        <section
          key={s.title}
          className="section how-deep faq-deep"
          style={{ background: idx % 2 === 0 ? 'var(--bg-sunk)' : 'var(--bg)' }}
        >
          <div className="section-inner">
            <div className="how-deep-head">
              <div className={`how-deep-stamp n${(idx % 3) + 1}`}>
                {String(idx + 1).padStart(2, '0')}
              </div>
              <div><h2 className="h1">{s.title}</h2></div>
            </div>
            <div className="about-section-body">
              {s.intro && <p className="about-body">{s.intro}</p>}
              {s.body && (
                <p className="about-body" dangerouslySetInnerHTML={{ __html: s.body }} />
              )}
              {s.items && (
                <ul className="about-list">
                  {s.items.map((it) => (
                    <li key={it.lead}>
                      <strong>{it.lead}.</strong> {it.body}
                    </li>
                  ))}
                </ul>
              )}
              {s.footer && (
                <p
                  className="about-body"
                  style={{ marginTop: 24 }}
                  dangerouslySetInnerHTML={{ __html: s.footer }}
                />
              )}
            </div>
          </div>
        </section>
      ))}

      <section className="section how-cta">
        <div className="section-inner">
          <div className="how-cta-card faq-cta-card">
            <div className="how-cta-body" style={{ padding: '56px' }}>
              <h2 className="h1 how-cta-h1">{t('cta.heading')}</h2>
              <p className="lede how-cta-lede">{t('cta.body')}</p>
              <div className="how-cta-row">
                <a href="mailto:support@nimvoy.com" className="btn primary lg">
                  {t('cta.button')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
